import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { normalizeGuid, guidEquals } from "../../utils/guid";
import EntityPicker from "../EntityPicker/EntityPicker";
import CommunityPermissionsV2Panel from "../CommunityPermissionsV2Panel/CommunityPermissionsV2Panel";
import { permissionsApiV2 } from "../../security/v2/apiClient";
import { Tray } from "../Tray/Tray";
import styles from "./CommunitySettingsModal.module.css";
import {
  fetchAllCommunityEntitiesAdmin,
  fetchAllRelationships,
  fetchAllOrgLevelTaxonomies,
  createCommunityEntity,
  updateCommunityEntity,
  createRelationship,
  removeRelationship,
} from "../../api";
import type {
  CommunityEntityResponse,
  CommunityEntityRelationshipResponse,
  OrgLevelTaxonomyResponse,
} from "../../api";

/* ================================================================
   Props
   ================================================================ */
interface Props {
  communityGuid?: string;
  /** DEV-892. Threaded into CommunityPermissionsV2Panel for the workflow
   *  request `created_by` field when emitting role-invitation notifications. */
  currentUserGuid?: string;
}

/* ================================================================
   Constants
   ================================================================ */
// DEV-1199 (2026-06-02). Fallback list used by `levelOptionsForParent`
// when the parent's per-org `org_level_taxonomy` is not yet seeded (or
// not resolvable). Constrained to a single valid value — `'organization'`
// — because everything else here ships straight to the DB as the row's
// entity_type, and the CHECK constraint in migration 213 rejects any
// value that isn't `'organization'`, `'solo_creator_workshop'`, or
// prefixed with `org_` / `geo_`.
//
// Prior to DEV-1199 this list also included bare `'club'`, `'school'`,
// `'hospital'`, `'affinity_group'` — these landed in the database as
// malformed entity_type values (Newport Rotary Club was a casualty,
// saved as `'club'` instead of `'org_rotary_club'`).
//
// If you need a specific sub-type (e.g. "Rotary Club" under Rotary
// International), seed it via `org_level_taxonomy` so it appears with
// the canonical `org_<level>` prefix. Do NOT add bare values back here.
const ORG_TYPES = ["organization"] as const;

const RELATIONSHIP_TYPES = [
  "parent_of",
  "headquartered_in",
  "operates_in",
  "member_of",
] as const;

function typeLabel(t: string): string {
  return t
    .replace(/^geo_/, "")
    .replace(/^org_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function typeBadge(t: string): { label: string; color: string } {
  // Geo entities are managed centrally via Manage Geographies (gear menu).
  // This tab should only show org entities, but keep the badge logic for safety.
  if (t.startsWith("geo_")) return { label: "Geo", color: "var(--accent-primary)" };
  return { label: "Org", color: "var(--color-warning)" };
}

function activeBadge(isActive: boolean): { label: string; bg: string; color: string } {
  return isActive
    ? { label: "Active", bg: "var(--success-bg)", color: "var(--color-success)" }
    : { label: "Location Only", bg: "var(--bg-hover)", color: "var(--text-tertiary)" };
}

function relLabel(t: string): string {
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Natural / numeric-aware string comparison so "Zone 4" sorts before
// "Zone 34" and "District 50" sorts before "District 1110". Use this
// everywhere we sort entity names or labels that may contain mixed
// text + numbers. Behavior delegates to Intl.Collator via localeCompare
// with numeric:true.
function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

/* ================================================================
   DEV-888 — Slug editor sub-component
   ================================================================ */
import { slugifyName } from "../../api";

function SlugField({
  value,
  name,
  onChange,
  communityGuid,
  entityName,
  disabled,
}: {
  value: string;
  name: string;
  onChange: (v: string) => void;
  communityGuid?: string;
  entityName: string;
  disabled?: boolean;
}) {
  const suggestion = React.useMemo(() => slugifyName(name), [name]);
  const showSuggest = !value.trim() && !!suggestion;
  const charsetOk = !value.trim() || /^[a-z0-9]+(-[a-z0-9]+)*$/.test(value.trim());
  const lenOk = !value.trim() || value.trim().length <= 64;
  const valid = charsetOk && lenOk;

  // Build a URL preview. For top-level entities (no parent context) the
  // preview is `life4earth.com/<slug>`; otherwise `life4earth.com/.../<slug>`
  // — the second segment is shown as ellipsis since the live editor doesn't
  // know its org-root slug at edit time.
  const previewSlug = (value.trim() || suggestion) || entityName.toLowerCase().replace(/\s+/g, "-");
  const previewUrl = communityGuid
    ? `life4earth.com/.../${previewSlug || "<slug>"}`
    : `life4earth.com/${previewSlug || "<slug>"}`;

  return (
    <div style={{ marginBlockStart: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBlockEnd: 4 }}>
        <span style={{ fontSize: 13, color: "var(--text-primary)" }}>URL Slug</span>
        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
          (lowercase letters, digits, hyphens; max 64)
        </span>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={suggestion || "e.g. jamestown"}
          disabled={disabled}
          maxLength={64}
          style={{
            flex: 1,
            padding: "8px 10px",
            borderRadius: 8,
            border: `1px solid ${valid ? "var(--border-subtle)" : "rgba(248,113,113,0.55)"}`,
            background: "var(--bg-tertiary)",
            color: "var(--text-primary)",
            fontSize: 13,
            outline: "none",
          }}
        />
        {showSuggest && (
          <button
            type="button"
            onClick={() => onChange(suggestion)}
            disabled={disabled}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid var(--border-default)",
              background: "transparent",
              color: "var(--text-primary)",
              fontSize: 12,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Use "{suggestion}"
          </button>
        )}
      </div>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBlockStart: 4 }}>
        Preview: <code>{previewUrl}</code>
      </div>
      {!valid && (
        <div style={{ fontSize: 11, color: "rgba(252,165,165,1)", marginBlockStart: 4 }}>
          {!charsetOk
            ? "Invalid characters. Use lowercase letters, digits, and hyphens only (no leading/trailing hyphens)."
            : "Slug exceeds 64 characters."}
        </div>
      )}
    </div>
  );
}

/* ================================================================
   Component
   ================================================================ */
export default function CommunityEntitiesTab({ communityGuid, currentUserGuid }: Props) {
  const { t } = useAppTranslation('community');
  /* ── state ── */
  const [entities, setEntities] = useState<CommunityEntityResponse[]>([]);
  const [relationships, setRelationships] = useState<CommunityEntityRelationshipResponse[]>([]);
  const [taxonomies, setTaxonomies] = useState<OrgLevelTaxonomyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // DEV-861 tier 2 — set of community entity GUIDs (upper-cased) that have
  // at least one active "leadership" grant (admin/owner-class bundle or a
  // scoped sysadmin like Rotary Org Admin). Renders as a small green dot
  // next to the entity name. Lets admins scan the tree and see at a glance
  // which clubs / districts have someone appointed.
  const [ledScopeGuids, setLedScopeGuids] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const guids = await permissionsApiV2.communityLeadershipPresence();
        if (cancelled) return;
        setLedScopeGuids(new Set(guids.map((g) => String(g || "").toUpperCase())));
      } catch {
        // Non-fatal: dot indicators simply won't render if the endpoint
        // is unavailable. The rest of the tree still works.
        if (!cancelled) setLedScopeGuids(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Search
  const [search, setSearch] = useState("");
  // Hide inactive (location-only) entities — useful after bulk-hiding to
  // collapse the noise out of the working view.
  const [hideInactive, setHideInactive] = useState(false);

  // Create / Edit form
  const [editing, setEditing] = useState<{
    entityGuid: string | null; // null = create
    name: string;
    entityType: string;
    visibility: string;
    code: string;
    isActive: boolean;
    slug: string; // DEV-888 — empty string = unset
  } | null>(null);

  // Parent selection for new sub-community
  const [parentGuid, setParentGuid] = useState<string>("");

  // Relationship management
  const [showRelForm, setShowRelForm] = useState<string | null>(null); // entityGuid to add rel
  const [relTarget, setRelTarget] = useState("");
  const [relType, setRelType] = useState<string>("parent_of");
  const [relDirection, setRelDirection] = useState<"child_of" | "parent_of">("child_of");

  // Expanded rows for hierarchy
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Confirmation dialog state
  const [confirm, setConfirm] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  // Edit-parent inline form state
  const [editingParent, setEditingParent] = useState<{
    entityGuid: string;
    currentParentGuid: string | null;
    newParentGuid: string;
  } | null>(null);

  // Auto-dismiss success
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(null), 3000);
    return () => clearTimeout(t);
  }, [successMsg]);

  // Track whether we've performed the one-shot auto-expand on first load
  // so that user-driven collapses persist (we only auto-expand once when
  // the entity list first arrives).
  const [autoExpandedRoots, setAutoExpandedRoots] = useState(false);

  /* ── data loading ──
   * Silent reload: when called from a save handler, pass silent=true so we
   * don't flip the loading flag — flipping it replaces the table with a
   * "Loading…" message on every save, which dumps the user's scroll position
   * back to the top. Initial mount uses silent=false so the first paint
   * shows the loading state correctly. */
  const loadData = useCallback(async (silent: boolean = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [ents, rels, taxos] = await Promise.all([
        fetchAllCommunityEntitiesAdmin().catch(() => [] as CommunityEntityResponse[]),
        fetchAllRelationships().catch(() => [] as CommunityEntityRelationshipResponse[]),
        fetchAllOrgLevelTaxonomies().catch(() => [] as OrgLevelTaxonomyResponse[]),
      ]);
      // Filter to org-only — geo entities are managed centrally via Manage Geographies
      setEntities(ents.filter((e) => !e.entityType.startsWith("geo_")));
      setRelationships(rels);
      setTaxonomies(taxos);
    } catch (err: any) {
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── derived data ── */
  const entityByGuid = useMemo(() => {
    const m = new Map<string, CommunityEntityResponse>();
    for (const e of entities) m.set(normalizeGuid(e.entityGuid), e);
    return m;
  }, [entities]);

  // Build parent→children map (only "parent_of"/"contains" relationships)
  // Keys and values are normalized via normalizeGuid so the map matches the
  // entityByGuid map and renderEntityRow lookups regardless of how the API
  // serialized casing on a given row. Without this, an uppercase guid in the
  // relationship row vs lowercase in the entity row would mis-key the lookup
  // and cause expanders to vanish (DEV-854 testing surfaced this).
  const childrenByParent = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const r of relationships) {
      if (r.relationshipType !== "parent_of" && r.relationshipType !== "contains") continue;
      const parent = normalizeGuid(r.parentEntityGuid);
      const child  = normalizeGuid(r.childEntityGuid);
      if (!parent || !child) continue;
      const list = m.get(parent) || [];
      list.push(child);
      m.set(parent, list);
    }
    return m;
  }, [relationships]);

  // Child→parent map (also GUID-normalized — see comment on childrenByParent)
  const parentOfChild = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of relationships) {
      if (r.relationshipType !== "parent_of" && r.relationshipType !== "contains") continue;
      const parent = normalizeGuid(r.parentEntityGuid);
      const child  = normalizeGuid(r.childEntityGuid);
      if (!parent || !child) continue;
      m.set(child, parent);
    }
    return m;
  }, [relationships]);

  // ── DEV-865 Phase 3b: per-org level taxonomy ──
  // Map org_root_entity_guid → list of taxonomy levels for that org.
  // The frontend reads this to populate the create-form's entity_type
  // dropdown with the org's own levels (Zone/District/Club/Interact for
  // Rotary, Region/Office/Team for a corporate org, etc.) instead of
  // the hardcoded global ORG_TYPES list.
  const taxonomyByRoot = useMemo(() => {
    const m = new Map<string, OrgLevelTaxonomyResponse[]>();
    for (const tx of taxonomies) {
      const root = normalizeGuid(tx.orgRootEntityGuid);
      if (!root) continue;
      const list = m.get(root) || [];
      list.push(tx);
      m.set(root, list);
    }
    return m;
  }, [taxonomies]);

  // Walk parentOfChild from the given guid up to the root (no parent).
  // Safety-bounded at 12 hops — real org trees max out around 5 levels;
  // a higher bound just makes accidental cycles fail gracefully rather
  // than infinite-loop.
  const findOrgRoot = useCallback((startGuid: string | null | undefined): string => {
    let current = normalizeGuid(startGuid);
    if (!current) return "";
    for (let i = 0; i < 12; i++) {
      const parent = parentOfChild.get(current);
      if (!parent) return current;
      current = parent;
    }
    return current;
  }, [parentOfChild]);

  // Compute the entity_type options for the create form's dropdown, driven
  // by the current parent's per-org taxonomy. Falls back to the global
  // ORG_TYPES list when:
  //   - parentGuid is empty (top-level create or edit mode)
  //   - the parent entity isn't in the loaded set
  //   - no taxonomy has been seeded for the parent's org root
  //   - the parent's level_key can't be derived from its entity_type
  // In all fallback cases the current behavior is preserved, so this
  // change is non-disruptive when no taxonomy exists yet.
  const levelOptionsForParent = useMemo((): { value: string; label: string }[] => {
    const fallback = ORG_TYPES.map((tt) => ({ value: tt, label: typeLabel(tt) }));
    if (!parentGuid) return fallback;

    const parent = entityByGuid.get(normalizeGuid(parentGuid));
    if (!parent) return fallback;

    const orgRoot = findOrgRoot(parentGuid);
    const orgTaxonomy = taxonomyByRoot.get(orgRoot) || [];
    if (orgTaxonomy.length === 0) return fallback;

    // If parent IS the org root (top of its own tree), valid children are
    // levels with parent_level_key = null. Otherwise derive parent's
    // level_key from its entity_type (strip 'org_' prefix) and find levels
    // whose parentLevelKey matches.
    const parentNorm = normalizeGuid(parentGuid);
    const parentIsOrgRoot = parentNorm === orgRoot;

    let validLevels: OrgLevelTaxonomyResponse[];
    if (parentIsOrgRoot) {
      validLevels = orgTaxonomy.filter((l) => l.parentLevelKey === null || l.parentLevelKey === undefined);
    } else {
      const parentType = parent.entityType;
      const parentLevelKey = parentType.startsWith("org_") ? parentType.slice(4) : null;
      if (!parentLevelKey) return fallback;
      validLevels = orgTaxonomy.filter((l) => l.parentLevelKey === parentLevelKey);
    }

    if (validLevels.length === 0) return fallback;

    return validLevels
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder || naturalCompare(a.levelLabel, b.levelLabel))
      .map((l) => ({ value: `org_${l.levelKey}`, label: l.levelLabel }));
  }, [parentGuid, entityByGuid, taxonomyByRoot, findOrgRoot]);

  // DEV-865 Phase 3b: when the parent changes during create, the set of
  // valid level options changes too. If the currently-selected entity_type
  // isn't in the new option set, default it to the first valid option so
  // the user doesn't see a stale/invalid type pre-filled. Only runs in
  // create mode (editing.entityGuid is null); edits don't auto-shift type.
  // Declared AFTER levelOptionsForParent to avoid a temporal-dead-zone
  // ReferenceError on initial render.
  useEffect(() => {
    if (!editing) return;
    if (editing.entityGuid) return;
    if (levelOptionsForParent.length === 0) return;
    const isValid = levelOptionsForParent.some((opt) => opt.value === editing.entityType);
    if (!isValid) {
      setEditing((prev) => prev ? { ...prev, entityType: levelOptionsForParent[0].value } : prev);
    }
    // editing intentionally omitted from deps — we only want this to react
    // to parent / option-set changes, not to every keystroke in the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentGuid, levelOptionsForParent]);

  // Cross-link relationships (non-hierarchy)
  const crossLinks = useMemo(() => {
    return relationships.filter(
      (r) => r.relationshipType !== "parent_of" && r.relationshipType !== "contains"
    );
  }, [relationships]);

  const crossLinksByEntity = useMemo(() => {
    const m = new Map<string, CommunityEntityRelationshipResponse[]>();
    for (const r of crossLinks) {
      const list1 = m.get(r.parentEntityGuid) || [];
      list1.push(r);
      m.set(r.parentEntityGuid, list1);
      const list2 = m.get(r.childEntityGuid) || [];
      list2.push(r);
      m.set(r.childEntityGuid, list2);
    }
    return m;
  }, [crossLinks]);

  // DEV-894. Set of entity GUIDs that have at least one active descendant
  // anywhere in their subtree. Used by the Hide Inactive filter so inactive
  // intermediate ancestors (e.g., Zones, Districts) remain visible as
  // containers when their child clubs have been activated. Without this,
  // unhiding a club doesn't make the club appear because the recursion
  // stops at the first inactive ancestor.
  const guidsWithActiveDescendants = useMemo(() => {
    const out = new Set<string>();
    const visit = (guid: string, depth = 0): boolean => {
      if (depth > 12) return false;
      const norm = normalizeGuid(guid);
      const childGuids = childrenByParent.get(norm) || [];
      for (const cg of childGuids) {
        const child = entityByGuid.get(cg);
        if (!child) continue;
        if (child.isActive) {
          out.add(norm);
          return true;
        }
        if (visit(child.entityGuid, depth + 1)) {
          out.add(norm);
          return true;
        }
      }
      return false;
    };
    for (const e of entities) visit(e.entityGuid);
    return out;
  }, [entities, childrenByParent, entityByGuid]);

  // Root entities (no parent in hierarchy) that match current community or all.
  // Set + filter both use normalized GUIDs to match the keys in childrenByParent.
  // Also respects the "hide inactive" toggle — inactive roots are dropped
  // entirely (children-of-inactive-roots also disappear since the recursion
  // never visits them).
  //
  // DEV-882: When a specific community is in scope (communityGuid prop is set
  // by CommunitySettingsModal), the tree starts at that one community — its
  // descendants are visited via the existing childrenByParent recursion. This
  // is what stops other-org trees (Rotary's Zones/Districts/Clubs, Red Cross,
  // etc.) from leaking into geo-community Sub-Communities views. When unscoped
  // (admin-wide platform view), fall through to the original "all parentless
  // entities" behavior.
  const rootEntities = useMemo(() => {
    if (communityGuid) {
      const norm = normalizeGuid(communityGuid);
      const found = entities.find((e) => normalizeGuid(e.entityGuid) === norm);
      if (!found) return [];
      if (hideInactive && !found.isActive) return [];
      return [found];
    }
    const childGuids = new Set<string>();
    for (const r of relationships) {
      if (r.relationshipType === "parent_of" || r.relationshipType === "contains") {
        const child = normalizeGuid(r.childEntityGuid);
        if (child) childGuids.add(child);
      }
    }
    return entities.filter((e) =>
      !childGuids.has(normalizeGuid(e.entityGuid)) &&
      (!hideInactive || e.isActive)
    );
  }, [communityGuid, entities, relationships, hideInactive]);

  // Auto-expand root communities on first load so the next level (zones,
  // top-level departments, etc.) is visible without an extra click. Fires
  // once per mount when entities first arrive; subsequent user-driven
  // collapses persist normally. Declared AFTER rootEntities to avoid a
  // temporal-dead-zone ReferenceError on initial render.
  useEffect(() => {
    if (autoExpandedRoots) return;
    if (rootEntities.length === 0) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const e of rootEntities) {
        if (e.entityGuid) next.add(e.entityGuid);
      }
      return next;
    });
    setAutoExpandedRoots(true);
  }, [rootEntities, autoExpandedRoots]);

  // Filtered entities (flat, for search). Also applies the hideInactive
  // toggle when active so search results don't surface hidden entities.
  const filteredEntities = useMemo(() => {
    const q = search.trim().toLowerCase();
    let pool = entities;
    if (hideInactive) pool = pool.filter((e) => e.isActive);
    if (!q) return pool;
    return pool.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.code.toLowerCase().includes(q) ||
        e.entityType.toLowerCase().includes(q)
    );
  }, [entities, search, hideInactive]);

  // Should we show hierarchy (no search active) or flat list (search active)?
  const showHierarchy = !search.trim();

  /* ── CRUD handlers ── */
  const startCreate = (parentEntityGuid?: string) => {
    setEditing({
      entityGuid: null,
      name: "",
      entityType: "organization",
      visibility: "public",
      code: "",
      isActive: true,
      slug: "",
    });
    setParentGuid(parentEntityGuid || "");
    setError(null);
  };

  const startEdit = (e: CommunityEntityResponse) => {
    setEditing({
      entityGuid: e.entityGuid,
      name: e.name,
      entityType: e.entityType,
      visibility: e.visibility,
      code: e.code,
      isActive: e.isActive,
      slug: e.slug ?? "",
    });
    setParentGuid("");
    setError(null);
  };

  const doSaveEntity = async () => {
    if (!editing) return;
    if (!editing.name.trim()) { setError("Name is required."); return; }
    setSaving(true);
    setError(null);
    try {
      if (editing.entityGuid) {
        await updateCommunityEntity(editing.entityGuid, {
          name: editing.name.trim(),
          entityType: editing.entityType,
          visibility: editing.visibility,
          code: editing.code.trim(),
          isActive: editing.isActive,
          // DEV-888: send slug as-is. Empty string clears; non-empty triggers
          // server-side validation (charset, reserved word, uniqueness).
          slug: editing.slug.trim(),
        });
        setSuccessMsg(`"${editing.name.trim()}" updated.`);
      } else {
        await createCommunityEntity({
          name: editing.name.trim(),
          entityType: editing.entityType,
          visibility: editing.visibility,
          code: editing.code.trim(),
          parentEntityGuid: parentGuid || undefined,
          relationshipType: parentGuid ? "parent_of" : undefined,
          slug: editing.slug.trim() || undefined,
        });
        setSuccessMsg(`"${editing.name.trim()}" created.`);
      }
      setEditing(null);
      await loadData(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  /** Wrap save in a confirmation dialog. */
  const saveEntity = () => {
    if (!editing) return;
    if (!editing.name.trim()) { setError("Name is required."); return; }

    // Duplicate-name check
    const nameLower = editing.name.trim().toLowerCase();
    const duplicate = entities.find(
      (e) => e.name.toLowerCase() === nameLower && normalizeGuid(e.entityGuid) !== normalizeGuid(editing.entityGuid)
    );

    const isCreate = !editing.entityGuid;
    const parentName = parentGuid ? entityByGuid.get(parentGuid)?.name : null;
    const activeLabel = editing.isActive ? "Active (community page)" : "Inactive (location only)";
    const typeStr = typeLabel(editing.entityType);

    const lines: string[] = [];
    if (isCreate) {
      lines.push(`Create "${editing.name.trim()}" as a ${typeStr}?`);
      if (parentName) lines.push(`Parent: ${parentName}`);
      lines.push(`Status: ${activeLabel}`);
      if (duplicate) lines.push(`\u26A0 A community named "${duplicate.name}" already exists. Are you sure this isn't a duplicate?`);
    } else {
      lines.push(`Update "${editing.name.trim()}"?`);
      lines.push(`Status: ${activeLabel}`);
    }

    setConfirm({
      title: isCreate ? "Confirm New Community" : "Confirm Update",
      message: lines.join("\n"),
      onConfirm: doSaveEntity,
    });
  };

  /** Edit parent — move entity to a new parent in the hierarchy. */
  const saveParentChange = async () => {
    if (!editingParent) return;
    const { entityGuid, currentParentGuid, newParentGuid } = editingParent;
    setSaving(true);
    setError(null);
    try {
      // Remove old parent relationship if it exists
      if (currentParentGuid) {
        await removeRelationship(currentParentGuid, entityGuid, "parent_of").catch(() => {
          // Try "contains" if "parent_of" fails
          return removeRelationship(currentParentGuid, entityGuid, "contains");
        });
      }
      // Create new parent relationship (if not moving to root)
      if (newParentGuid) {
        await createRelationship({
          parentEntityGuid: newParentGuid,
          childEntityGuid: entityGuid,
          relationshipType: "parent_of",
        });
      }
      const entName = entityByGuid.get(entityGuid)?.name || entityGuid;
      const newParentName = newParentGuid ? (entityByGuid.get(newParentGuid)?.name || newParentGuid) : "root (no parent)";
      setSuccessMsg(`"${entName}" moved to ${newParentName}.`);
      setEditingParent(null);
      await loadData(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  /** Hide (kill switch) — immediately deactivate + set visibility to private. */
  // Toggle Hide ↔ Unhide. "Hidden" = isActive=false (location-only). The
  // symmetric Unhide restores both isActive=true AND visibility='public', so
  // the row is fully visible again. If the user wants finer-grained state
  // (active but private, etc.) they can use the status pill toggle and the
  // visibility dropdown in the edit form independently.
  const toggleHide = (e: CommunityEntityResponse) => {
    const isCurrentlyHidden = !e.isActive;

    let title: string;
    let msg: string;
    if (isCurrentlyHidden) {
      title = "Unhide Community";
      msg = `This will reactivate "${e.name}" — it will get a community page back and visibility will return to public. Continue?`;
    } else {
      const childCount = (childrenByParent.get(normalizeGuid(e.entityGuid)) || []).length;
      title = "Hide Community";
      msg = childCount > 0
        ? `This will hide "${e.name}" and deactivate its community page. It has ${childCount} sub-communit${childCount === 1 ? "y" : "ies"} — they will NOT be hidden automatically. Continue?`
        : `This will hide "${e.name}" — it will be deactivated (no community page) and set to private visibility. It will still appear in this admin list. Continue?`;
    }

    setConfirm({
      title,
      message: msg,
      onConfirm: async () => {
        setSaving(true);
        setError(null);
        try {
          // DEV-890: decouple is_active from visibility. Activation creates a
          // page; visibility (public/private) is the community admin's
          // separate decision via Audience & Permissions. Previously these
          // were set together, which forced half-set-up clubs public the
          // moment org admin activated them.
          if (isCurrentlyHidden) {
            await updateCommunityEntity(e.entityGuid, { isActive: true });
            setSuccessMsg(
              `"${e.name}" is now active. The community admin still controls public visibility from Audience & Permissions.`
            );
          } else {
            await updateCommunityEntity(e.entityGuid, { isActive: false });
            setSuccessMsg(`"${e.name}" is now hidden.`);
          }
          await loadData(true);
        } catch (err: any) {
          setError(err.message);
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const addCrossLink = async (entityGuid: string) => {
    if (!relTarget) return;
    setSaving(true);
    setError(null);
    try {
      const req =
        relDirection === "child_of"
          ? { parentEntityGuid: relTarget, childEntityGuid: entityGuid, relationshipType: relType }
          : { parentEntityGuid: entityGuid, childEntityGuid: relTarget, relationshipType: relType };
      await createRelationship(req);
      setShowRelForm(null);
      setRelTarget("");
      setRelType("parent_of");
      setSuccessMsg("Relationship added.");
      await loadData(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeCrossLink = async (r: CommunityEntityRelationshipResponse) => {
    setSaving(true);
    try {
      await removeRelationship(r.parentEntityGuid, r.childEntityGuid, r.relationshipType);
      await loadData(true);
      setSuccessMsg("Relationship removed.");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const expandEntity = (guid: string) => {
    setExpanded((prev) => {
      if (prev.has(guid)) return prev;
      const next = new Set(prev);
      next.add(guid);
      return next;
    });
  };

  const collapseEntity = (guid: string) => {
    setExpanded((prev) => {
      if (!prev.has(guid)) return prev;
      const next = new Set(prev);
      next.delete(guid);
      return next;
    });
  };

  /* ── render helpers ── */

  // Quick-toggle active status inline — with confirmation
  const toggleActive = (e: CommunityEntityResponse) => {
    const willActivate = !e.isActive;
    setConfirm({
      title: willActivate ? "Activate Community" : "Deactivate Community",
      message: willActivate
        ? `Activating "${e.name}" will generate a public community page. Members will be able to join and content will be visible. Continue?`
        : `Deactivating "${e.name}" will remove its community page. It will still appear as a location label. Continue?`,
      onConfirm: async () => {
        setSaving(true);
        setError(null);
        try {
          await updateCommunityEntity(e.entityGuid, { isActive: !e.isActive });
          setSuccessMsg(`"${e.name}" is now ${willActivate ? "active (community page)" : "location only"}.`);
          await loadData(true);
        } catch (err: any) {
          setError(err.message);
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const renderEntityRow = (e: CommunityEntityResponse, depth: number) => {
    const normEg = normalizeGuid(e.entityGuid);
    const children = childrenByParent.get(normEg) || [];
    const hasChildren = children.length > 0;
    const isExpanded = expanded.has(e.entityGuid);
    const badge = typeBadge(e.entityType);
    const aBadge = activeBadge(e.isActive);
    const links = crossLinksByEntity.get(e.entityGuid) || [];
    const rowOpacity = e.isActive ? 1 : 0.6;

    return (
      <React.Fragment key={e.entityGuid}>
        <tr style={{ opacity: rowOpacity }}>
          <td style={{ paddingInlineStart: 16 + depth * 24 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {hasChildren && (
                <button
                  type="button"
                  onClick={() => isExpanded ? collapseEntity(e.entityGuid) : expandEntity(e.entityGuid)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--text-tertiary)",
                    cursor: "pointer",
                    fontSize: 11,
                    width: 16,
                    padding: 0,
                  }}
                >
                  {isExpanded ? "\u25BC" : "\u25B6"}
                </button>
              )}
              {!hasChildren && <span style={{ width: 16, display: "inline-block" }} />}
              <strong>{e.name}</strong>
              {/* DEV-861 tier 2 — leader-presence dot. Renders when this
                  entity has at least one active grant on a leadership
                  bundle (community:admin or scoped-sysadmin). Tooltip on
                  hover. Click flows through to Edit → V2 panel via the
                  existing per-row Edit action. */}
              {ledScopeGuids.has(normalizeGuid(e.entityGuid).toUpperCase()) && (
                <span
                  title={t('entities.hasLeaderTooltip', 'A leader has been appointed on this entity. Click Edit to see who.')}
                  aria-label={t('entities.hasLeaderTooltip', 'A leader has been appointed on this entity.')}
                  style={{
                    display: "inline-block",
                    width: 12,
                    height: 12,
                    borderRadius: 999,
                    background: "var(--color-success)",
                    marginInlineStart: 8,
                    verticalAlign: "middle",
                  }}
                />
              )}
              {/* DEV-890 — lock icon for private communities so admins can scan their tree
                  and see at a glance which clubs are still in setup mode */}
              {e.visibility === 'private' && e.isActive && (
                <span
                  title="Private — only members and admins can see this community"
                  aria-label="Private community"
                  style={{
                    marginInlineStart: 6,
                    fontSize: 11,
                    color: "var(--text-tertiary)",
                  }}
                >
                  🔒
                </span>
              )}
              {e.code && (
                <span style={{ opacity: 0.5, fontSize: 11, marginInlineStart: 4 }}>{e.code}</span>
              )}
              {/* DEV-888 — slug pill so admins can see at a glance which entities have URL slugs */}
              {e.slug && (
                <span
                  title={`URL slug: ${e.slug}`}
                  style={{
                    marginInlineStart: 6,
                    fontSize: 10,
                    fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                    padding: "1px 6px",
                    borderRadius: 999,
                    border: "1px solid var(--border-subtle)",
                    background: "var(--bg-tertiary)",
                    color: "var(--text-secondary)",
                  }}
                >
                  /{e.slug}
                </span>
              )}
              {hasChildren && (
                <span
                  style={{ opacity: 0.55, fontSize: 11, marginInlineStart: 6, color: "var(--text-tertiary)" }}
                  title={`${children.length} direct sub-${children.length === 1 ? "community" : "communities"}`}
                >
                  ({children.length})
                </span>
              )}
            </span>
          </td>
          <td>
            <span
              className={styles.pill}
              style={{
                background: badge.color,
                color: "#fff",
                fontSize: 10,
                padding: "2px 8px",
                borderRadius: 8,
              }}
            >
              {badge.label}
            </span>
            <span style={{ opacity: 0.6, fontSize: 11, marginInlineStart: 6 }}>
              {typeLabel(e.entityType)}
            </span>
          </td>
          <td>
            <button
              type="button"
              onClick={() => toggleActive(e)}
              disabled={saving}
              title={e.isActive ? "Active — generates a community page. Click to deactivate." : "Inactive — location label only. Click to activate."}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: aBadge.bg,
                color: aBadge.color,
                border: "1px solid " + (e.isActive ? "var(--success-border)" : "var(--border-subtle)"),
                borderRadius: 6,
                padding: "2px 10px",
                fontSize: 10,
                fontWeight: 600,
                cursor: "pointer",
                letterSpacing: "0.02em",
              }}
            >
              <span style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: e.isActive ? "var(--color-success)" : "var(--text-tertiary)",
                display: "inline-block",
                flexShrink: 0,
              }} />
              {aBadge.label}
            </button>
          </td>
          <td>
            {links.length > 0 && (
              <span style={{ fontSize: 11, opacity: 0.7 }}>
                {links.map((lnk, i) => {
                  const other =
                    lnk.parentEntityGuid === e.entityGuid
                      ? lnk.childEntityGuid
                      : lnk.parentEntityGuid;
                  const otherEnt = entityByGuid.get(other);
                  const dir =
                    lnk.parentEntityGuid === e.entityGuid
                      ? relLabel(lnk.relationshipType)
                      : relLabel(lnk.relationshipType) + " (of)";
                  return (
                    <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, marginInlineEnd: 8 }}>
                      <span className={styles.pillSoft} style={{ fontSize: 10 }}>
                        {dir}
                      </span>
                      {otherEnt?.name || other}
                      <button
                        type="button"
                        className={styles.btnMini}
                        onClick={() => removeCrossLink(lnk)}
                        style={{ fontSize: 10, padding: "1px 4px", opacity: 0.6 }}
                        title="Remove link"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </span>
            )}
          </td>
          <td style={{ width: 260, whiteSpace: "nowrap" }}>
            <button
              type="button"
              className={styles.btnMini}
              onClick={() => startEdit(e)}
              style={{ marginInlineEnd: 4 }}
              title="Edit name, type, code, visibility, and status"
            >
              Edit
            </button>
            <button
              type="button"
              className={styles.btnMini}
              onClick={() => {
                const currentParent = parentOfChild.get(normalizeGuid(e.entityGuid)) || null;
                setEditingParent({
                  entityGuid: e.entityGuid,
                  currentParentGuid: currentParent,
                  newParentGuid: currentParent || "",
                });
              }}
              style={{ marginInlineEnd: 4 }}
              title="Move this entity to a different parent"
            >
              Parent
            </button>
            <button
              type="button"
              className={styles.btnMini}
              onClick={() => startCreate(e.entityGuid)}
              style={{ marginInlineEnd: 4 }}
              title="Create a sub-community under this entity"
            >
              + Sub
            </button>
            <button
              type="button"
              className={styles.btnMini}
              onClick={() => {
                setShowRelForm(showRelForm === e.entityGuid ? null : e.entityGuid);
                setRelTarget("");
                setRelType("headquartered_in");
              }}
              style={{ marginInlineEnd: 4 }}
              title="Add a cross-link relationship"
            >
              Link
            </button>
            <button
              type="button"
              className={styles.btnMini}
              onClick={() => toggleHide(e)}
              style={{
                color: e.isActive ? "var(--color-error)" : "var(--color-success)",
                borderColor: e.isActive ? "var(--error-border)" : "var(--success-border)",
              }}
              title={
                e.isActive
                  ? "Kill switch — immediately deactivate and hide this community"
                  : "Restore — reactivate this community and make it visible again"
              }
            >
              {e.isActive ? "Hide" : "Unhide"}
            </button>
          </td>
        </tr>
        {/* Inline edit-parent form */}
        {guidEquals(editingParent?.entityGuid, e.entityGuid) && (
          <tr>
            <td colSpan={5} style={{ padding: "8px 16px 8px " + (40 + depth * 24) + "px" }}>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  background: "var(--accent-secondary-bg-soft)",
                  padding: "8px 12px",
                  borderRadius: 8,
                  fontSize: 12,
                  border: "var(--border-subtle)",
                }}
              >
                <span style={{ fontWeight: 600, color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                  Move to:
                </span>
                <EntityPicker
                  value={editingParent.newParentGuid}
                  onChange={(guid) =>
                    setEditingParent({ ...editingParent, newParentGuid: guid })
                  }
                  entities={entities}
                  excludeGuid={e.entityGuid}
                  emptyOptionLabel="— Root (no parent) —"
                  placeholder="Search by name…"
                  typeLabelFn={typeLabel}
                  disabled={saving}
                />
                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={saveParentChange}
                  disabled={saving || editingParent.newParentGuid === (editingParent.currentParentGuid || "")}
                  style={{ fontSize: 12, padding: "4px 12px" }}
                >
                  Save
                </button>
                <button
                  type="button"
                  className={styles.btnMini}
                  onClick={() => setEditingParent(null)}
                >
                  Cancel
                </button>
              </div>
            </td>
          </tr>
        )}
        {/* Inline relationship form */}
        {showRelForm === e.entityGuid && (
          <tr>
            <td colSpan={5} style={{ padding: "8px 16px 8px " + (40 + depth * 24) + "px" }}>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  background: "var(--bg-hover)",
                  padding: "8px 12px",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              >
                <select
                  className={styles.select}
                  value={relDirection}
                  onChange={(ev) => setRelDirection(ev.target.value as any)}
                  style={{ width: 110, fontSize: 12 }}
                >
                  <option value="child_of">is child of</option>
                  <option value="parent_of">is parent of</option>
                </select>
                <select
                  className={styles.select}
                  value={relType}
                  onChange={(ev) => setRelType(ev.target.value)}
                  style={{ width: 150, fontSize: 12 }}
                >
                  {RELATIONSHIP_TYPES.map((rt) => (
                    <option key={rt} value={rt}>
                      {relLabel(rt)}
                    </option>
                  ))}
                </select>
                <select
                  className={styles.select}
                  value={relTarget}
                  onChange={(ev) => setRelTarget(ev.target.value)}
                  style={{ flex: 1, fontSize: 12 }}
                >
                  <option value="">-- Select entity --</option>
                  {entities
                    .filter((x) => normalizeGuid(x.entityGuid) !== normalizeGuid(e.entityGuid))
                    .map((x) => (
                      <option key={x.entityGuid} value={x.entityGuid}>
                        {x.name} ({typeLabel(x.entityType)})
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={() => addCrossLink(e.entityGuid)}
                  disabled={!relTarget || saving}
                  style={{ fontSize: 12, padding: "4px 12px" }}
                >
                  Add
                </button>
                <button
                  type="button"
                  className={styles.btnMini}
                  onClick={() => setShowRelForm(null)}
                >
                  Cancel
                </button>
              </div>
            </td>
          </tr>
        )}
        {/* Children — children list already contains normalized GUIDs;
            entityByGuid is also keyed on normalized GUIDs, so the lookup works.
            hideInactive applies here too so an expanded parent doesn't reveal
            hidden children that would otherwise be filtered out at the root. */}
        {isExpanded &&
          children
            .map((cg) => entityByGuid.get(cg))
            // DEV-894. When Hide Inactive is on, keep inactive entities visible
            // if any descendant in their subtree is active — they serve as
            // containers/breadcrumbs to reach the active leaves. Their dim
            // opacity (`rowOpacity` below) signals "container only" visually.
            .filter((c): c is CommunityEntityResponse =>
              Boolean(c) &&
              (!hideInactive
                || c!.isActive
                || guidsWithActiveDescendants.has(normalizeGuid(c!.entityGuid))),
            )
            .sort((a, b) => naturalCompare(a.name, b.name))
            .map((child) => renderEntityRow(child, depth + 1))}
      </React.Fragment>
    );
  };

  /* ── main render ── */
  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: "center", opacity: 0.6 }}>
        {t('entities.loading')}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Success toast */}
      {successMsg && (
        <div
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            background: "var(--success-bg)",
            border: "1px solid var(--success-border)",
            color: "var(--color-success)",
            fontSize: 13,
          }}
        >
          {successMsg}
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            background: "var(--error-bg)",
            border: "1px solid var(--error-border)",
            color: "var(--color-error)",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* Confirmation dialog overlay.
          z-index 10010 sits above the Tray (10001) so the confirm
          renders on top of the open edit tray. Below the topbar's
          100000 so navigation chrome stays visible. */}
      {confirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10010,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.6)",
          }}
          onClick={() => setConfirm(null)}
        >
          <div
            onClick={(ev) => ev.stopPropagation()}
            style={{
              background: "var(--bg-secondary)",
              border: "1px solid var(--border-default)",
              borderRadius: 12,
              padding: "20px 24px",
              maxWidth: 420,
              width: "90%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10, color: "var(--text-primary)" }}>
              {confirm.title}
            </div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)", whiteSpace: "pre-line", lineHeight: 1.5, marginBottom: 16 }}>
              {confirm.message}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                className={styles.btnMini}
                onClick={() => setConfirm(null)}
                style={{ padding: "6px 16px", fontSize: 13 }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={() => {
                  confirm.onConfirm();
                  setConfirm(null);
                }}
                disabled={saving}
                style={{ padding: "6px 16px", fontSize: 13 }}
              >
                {saving ? "Saving..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar: search, filter, create */}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          className={styles.input}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('entities.search')}
          style={{ flex: 1 }}
        />
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: "var(--text-secondary)",
            cursor: "pointer",
            whiteSpace: "nowrap",
            userSelect: "none",
          }}
          title="Hide entities that are deactivated (location-only) — useful after bulk-hide to declutter."
        >
          <input
            type="checkbox"
            checked={hideInactive}
            onChange={(ev) => setHideInactive(ev.target.checked)}
          />
          Hide inactive
        </label>
        <button type="button" className={styles.btnPrimary} onClick={() => startCreate()}>
          + {t('common.new')} {t('common.subCommunity')}
        </button>
      </div>

      {/* DEV-893. Sub-community editing in a slide-in Tray instead of inline.
          Keeps the entity tree visible (with scrim) so admins maintain context
          while editing. Mirrors the KPI Wizard / Mission Sub-KPI Card pattern. */}
      <Tray
        open={editing !== null}
        onClose={() => !saving && setEditing(null)}
        width={720}
        // DEV-893. Suppress the Tray's own scrim because this Tray opens
        // INSIDE CommunitySettingsModal which already has a scrim at 0.65.
        // Layered scrims combine to ~0.79 effective alpha — near-blackout.
        // The Tray remains visually distinct via its own elevation/shadow,
        // and the modal's existing scrim handles parent dimming.
        scrim={false}
        title={
          editing ? (
            <span>
              {editing.entityGuid ? t('common.editEntity') : t('common.newEntity')}
              {parentGuid && (
                <span style={{ fontWeight: 400, color: "var(--text-tertiary)", marginInlineStart: 8, fontSize: 12 }}>
                  {t('common.subCommunityOf', { parent: entityByGuid.get(normalizeGuid(parentGuid))?.name || parentGuid })}
                </span>
              )}
            </span>
          ) : ""
        }
      >
        {editing && (
          <Tray.Body>
            <div style={{ padding: 16 }}>

          {/* DEV-854 testing feedback (option c): hierarchy breadcrumb on edit.
              Walks parentOfChild from the editing entity up to the root and
              renders the chain. Read-only — to change the parent, use the
              "Parent" action on the row. */}
          {editing.entityGuid && (() => {
            const norm = normalizeGuid(editing.entityGuid);
            const chain: string[] = [];
            let current = parentOfChild.get(norm);
            for (let i = 0; i < 12 && current; i++) {
              const ent = entityByGuid.get(current);
              if (ent) chain.unshift(ent.name);
              current = parentOfChild.get(current);
            }
            return (
              <div style={{
                fontSize: 11,
                color: "var(--text-tertiary)",
                marginBlockEnd: 12,
                paddingBlockStart: 4,
                paddingBlockEnd: 4,
              }}>
                <span style={{ opacity: 0.6, marginInlineEnd: 6 }}>Hierarchy:</span>
                {chain.length === 0 ? (
                  <span style={{ fontStyle: "italic", opacity: 0.7 }}>(top-level — no parent)</span>
                ) : (
                  <>
                    {chain.map((name, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span style={{ opacity: 0.4, marginInline: 4 }}>›</span>}
                        <span>{name}</span>
                      </React.Fragment>
                    ))}
                    <span style={{ opacity: 0.4, marginInline: 4 }}>›</span>
                    <strong style={{ color: "var(--text-secondary)" }}>
                      {editing.name || "(this entity)"}
                    </strong>
                  </>
                )}
              </div>
            );
          })()}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <div className={styles.field}>
              <div className={styles.labelRow}>
                <span className={styles.label}>{t('entities.nameRequired')}</span>
              </div>
              <input
                className={styles.input}
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="e.g. Nashville, Rotary District 7930"
                disabled={saving}
                autoFocus
              />
            </div>
            <div className={styles.field}>
              <div className={styles.labelRow}>
                <span className={styles.label}>{t('entities.entityType')}</span>
              </div>
              {/* DEV-1200: entity_type is immutable after creation. On
                  edit (editing.entityGuid != null), render as read-only
                  text. On create, show the dropdown. Sysadmin recourse
                  for misclassifications is direct SQL. */}
              {editing.entityGuid != null ? (
                <div className={styles.readOnlyValue}>
                  {typeLabel(editing.entityType)}
                  <span
                    className={styles.helpInline}
                    title={t('entities.entityTypeImmutableHelp', {
                      defaultValue:
                        'Entity type cannot be changed after creation. Contact a sysadmin if a misclassification needs to be fixed.',
                    }) as string}
                  >
                    &nbsp;(locked)
                  </span>
                </div>
              ) : (
                <select
                  className={styles.select}
                  value={editing.entityType}
                  onChange={(e) => setEditing({ ...editing, entityType: e.target.value })}
                  disabled={saving}
                >
                  {levelOptionsForParent.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px" }}>
            <div className={styles.field}>
              <div className={styles.labelRow}>
                <span className={styles.label}>{t('entities.code')}</span>
              </div>
              <input
                className={styles.input}
                value={editing.code}
                onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                placeholder="e.g. US-TN, D7930"
                disabled={saving}
              />
            </div>
            <div className={styles.field}>
              <div className={styles.labelRow}>
                <span className={styles.label}>{t('entities.visibility')}</span>
              </div>
              <select
                className={styles.select}
                value={editing.visibility}
                onChange={(e) => setEditing({ ...editing, visibility: e.target.value })}
                disabled={saving}
              >
                <option value="public">{t('entities.public')}</option>
                <option value="private">{t('entities.private')}</option>
              </select>
            </div>
            <div className={styles.field}>
              <div className={styles.labelRow}>
                <span className={styles.label}>Status</span>
                <span style={{ fontSize: 10, color: "var(--text-muted)", marginInlineStart: 6 }}>
                  {editing.isActive ? "Generates a community page" : "Location label only"}
                </span>
              </div>
              <select
                className={styles.select}
                value={editing.isActive ? "active" : "inactive"}
                onChange={(e) =>
                  setEditing({ ...editing, isActive: e.target.value === "active" })
                }
                disabled={saving}
              >
                <option value="active">Active — Community Page</option>
                <option value="inactive">Inactive — Location Only</option>
              </select>
            </div>
          </div>
          {/* DEV-888 — URL slug editor + live preview */}
          <SlugField
            value={editing.slug}
            name={editing.name}
            onChange={(v) => setEditing({ ...editing, slug: v })}
            communityGuid={communityGuid}
            entityName={editing.name}
            disabled={saving}
          />
          {!editing.entityGuid && !parentGuid && (
            <div className={styles.field}>
              <div className={styles.labelRow}>
                <span className={styles.label}>Parent Community (optional)</span>
              </div>
              <EntityPicker
                value={parentGuid}
                onChange={(guid) => setParentGuid(guid)}
                entities={entities}
                emptyOptionLabel="— No parent (root entity) —"
                placeholder="Search by name…"
                typeLabelFn={typeLabel}
                disabled={saving}
              />
            </div>
          )}

          {/* DEV-861 [U1]: community-scope role assignment (Owner/Admin/etc.)
              for the entity being edited. Renders only on edit (not create)
              because the entity must exist before grants can attach. */}
          {editing.entityGuid && (
            <CommunityPermissionsV2Panel
              communityGuid={editing.entityGuid}
              canAdmin={true}
              currentUserGuid={currentUserGuid}
              scopeLabel={editing.name || undefined}
            />
          )}

          {/* DEV-854 testing feedback (option c): cross-link relationships
              read-only list. Edits happen via the "Link" action on the row. */}
          {editing.entityGuid && (() => {
            const links = crossLinksByEntity.get(editing.entityGuid) || [];
            return (
              <div style={{
                marginBlockStart: 12,
                paddingBlockStart: 10,
                borderBlockStart: "1px solid var(--border-subtle)",
              }}>
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--text-secondary)",
                  marginBlockEnd: 6,
                }}>
                  Cross-links{" "}
                  {links.length > 0 && (
                    <span style={{ opacity: 0.6, fontWeight: 400 }}>({links.length})</span>
                  )}
                </div>
                {links.length === 0 ? (
                  <div style={{ fontSize: 11, opacity: 0.55, fontStyle: "italic" }}>
                    None. Use the "Link" action on the row to add cross-link
                    relationships (headquartered_in, operates_in, member_of).
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 11, display: "flex", flexDirection: "column", gap: 4 }}>
                      {links.map((lnk, i) => {
                        const isParent = lnk.parentEntityGuid === editing.entityGuid;
                        const otherGuid = isParent ? lnk.childEntityGuid : lnk.parentEntityGuid;
                        const otherEnt = entityByGuid.get(normalizeGuid(otherGuid));
                        const direction = isParent
                          ? relLabel(lnk.relationshipType)
                          : `${relLabel(lnk.relationshipType)} (of)`;
                        return (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span className={styles.pillSoft} style={{ fontSize: 10 }}>
                              {direction}
                            </span>
                            <span>{otherEnt?.name || otherGuid}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: 10, opacity: 0.5, marginBlockStart: 6, fontStyle: "italic" }}>
                      To add or remove cross-links, use the "Link" action on the row in the table.
                    </div>
                  </>
                )}
              </div>
            );
          })()}

            </div>
          </Tray.Body>
        )}
        {editing && (
          <Tray.Footer>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", padding: 12 }}>
              <button
                type="button"
                className={styles.btnMini}
                onClick={() => setEditing(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={saveEntity}
                disabled={saving}
              >
                {saving ? "Saving..." : editing.entityGuid ? "Update" : "Create"}
              </button>
            </div>
          </Tray.Footer>
        )}
      </Tray>

      {/* Entity table */}
      <div className={styles.memberTableWrap}>
        <table className={styles.memberTable}>
          <thead>
            <tr>
              <th>{t('entities.nameHeader')}</th>
              <th style={{ width: 160 }}>{t('entities.typeHeader')}</th>
              <th style={{ width: 130 }}>{t('entities.statusHeader')}</th>
              <th>{t('common.relationships')}</th>
              <th style={{ width: 200 }}>{t('entities.actionsHeader')}</th>
            </tr>
          </thead>
          <tbody>
            {showHierarchy ? (
              rootEntities.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: 24 }}>
                    <div style={{ opacity: 0.6, fontSize: 13 }}>
                      {t('entities.noSubCommunities')}
                    </div>
                  </td>
                </tr>
              ) : (
                rootEntities
                  .sort((a, b) => naturalCompare(a.name, b.name))
                  .map((e) => renderEntityRow(e, 0))
              )
            ) : filteredEntities.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: 24 }}>
                  <div style={{ opacity: 0.6, fontSize: 13 }}>{t('entities.noMatches')}</div>
                </td>
              </tr>
            ) : (
              filteredEntities
                .sort((a, b) => naturalCompare(a.name, b.name))
                .map((e) => renderEntityRow(e, 0))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer info */}
      <div style={{ fontSize: 11, color: "var(--text-muted)", padding: "4px 0" }}>
        {entities.length} sub-communities · Geographic communities are managed centrally via
        the gear menu → Manage Geographies.
      </div>
    </div>
  );
}
